/**
 * App Switcher dropdown component
 * Allows switching between platform apps from sidebar header
 */

import { useNavigate } from 'react-router-dom'
import { FileText, Search, BotMessageSquare, BarChart3, Users, Home, ChevronsUpDown, GraduationCap } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenuButton } from '@/components/ui/sidebar'
import type { PlatformApp } from '@/types/platform'
import { PLATFORM_APPS } from '@/types/platform'

interface AppSwitcherApp {
  readonly id: PlatformApp
  readonly displayName: string
  readonly description: string
  readonly icon: LucideIcon
  readonly path: string
}

const ICON_MAP: Record<string, LucideIcon> = {
  FileText,
  Search,
  BotMessageSquare,
  BarChart3,
  Users,
  GraduationCap,
}

const switcherApps: readonly AppSwitcherApp[] = (
  ['discovery', 'identity', 'pac', 'analytics', 'polix-ai', 'docs'] as const
).map((id) => {
  const config = PLATFORM_APPS[id]
  const pathOverrides: Partial<Record<PlatformApp, string>> = {
    'polix-ai': config.basePath,
    analytics: config.basePath,
    docs: '/docs/getting-started',
  }
  return {
    id,
    displayName: config.displayName,
    description: config.description,
    icon: ICON_MAP[config.iconName] ?? FileText,
    path: pathOverrides[id] ?? `${config.basePath}/dashboard`,
  }
})

interface AppSwitcherProps {
  readonly currentApp: PlatformApp
}

export function AppSwitcher({ currentApp }: AppSwitcherProps) {
  const navigate = useNavigate()

  const currentConfig = PLATFORM_APPS[currentApp]
  const CurrentIcon = ICON_MAP[currentConfig.iconName] ?? FileText

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <CurrentIcon className="size-4" />
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{currentConfig.displayName}</span>
            <span className="truncate text-xs text-muted-foreground">poli-x Platform</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Switch App
        </DropdownMenuLabel>
        {switcherApps.map((app) => {
          const isActive = app.id === currentApp
          return (
            <DropdownMenuItem
              key={app.id}
              onClick={() => navigate(app.path)}
              disabled={isActive}
              className="gap-2 p-2"
            >
              <div className="flex size-6 items-center justify-center rounded-sm border">
                <app.icon className="size-4 shrink-0" />
              </div>
              <div className="grid flex-1 leading-tight">
                <span className="text-sm font-medium">{app.displayName}</span>
                <span className="text-xs text-muted-foreground">{app.description}</span>
              </div>
            </DropdownMenuItem>
          )
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/')} className="gap-2 p-2">
          <div className="flex size-6 items-center justify-center rounded-sm border">
            <Home className="size-4 shrink-0" />
          </div>
          <span className="text-sm font-medium">Back to Launchpad</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
